/**
 * The single source of truth for iconography.
 *
 * Every icon in the app comes from here so that one concept always gets one
 * drawing at one weight. Object and navigation icons use MUI's `Outlined`
 * family; glyph-like controls (close, chevrons, send) use `Rounded`, whose
 * round line caps sit better next to text at small sizes.
 *
 * Icons are decorative by default — they sit beside a visible label or inside
 * a control that carries its own `aria-label`. Pass `title` only when the icon
 * is the sole carrier of meaning.
 */
/* Type-only, so the barrel import is erased at build time. */
import type { SvgIconComponent } from '@mui/icons-material';

/* -------------------------------- Navigation -------------------------------- */
import HomeOutlined from '@mui/icons-material/HomeOutlined';
import MeetingRoomOutlined from '@mui/icons-material/MeetingRoomOutlined';
import ViewKanbanOutlined from '@mui/icons-material/ViewKanbanOutlined';
import TaskAltOutlined from '@mui/icons-material/TaskAltOutlined';
import BoltOutlined from '@mui/icons-material/BoltOutlined';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
import VideocamOutlined from '@mui/icons-material/VideocamOutlined';
import DrawOutlined from '@mui/icons-material/DrawOutlined';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import CreditCardOutlined from '@mui/icons-material/CreditCardOutlined';
import BadgeOutlined from '@mui/icons-material/BadgeOutlined';
import DiamondOutlined from '@mui/icons-material/DiamondOutlined';
import KeyOutlined from '@mui/icons-material/KeyOutlined';
import PhotoLibraryOutlined from '@mui/icons-material/PhotoLibraryOutlined';
import SportsEsportsOutlined from '@mui/icons-material/SportsEsportsOutlined';
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import CloudOutlined from '@mui/icons-material/CloudOutlined';

/* --------------------------------- Controls --------------------------------- */
import MenuRounded from '@mui/icons-material/MenuRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import GridViewRounded from '@mui/icons-material/GridViewRounded';
import ViewListRounded from '@mui/icons-material/ViewListRounded';
import SwapVertRounded from '@mui/icons-material/SwapVertRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import RemoveRounded from '@mui/icons-material/RemoveRounded';
import CheckRounded from '@mui/icons-material/CheckRounded';
import MoreHorizRounded from '@mui/icons-material/MoreHorizRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import StarBorderRounded from '@mui/icons-material/StarBorderRounded';
import StarRounded from '@mui/icons-material/StarRounded';
import DragIndicatorRounded from '@mui/icons-material/DragIndicatorRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import KeyboardArrowDownRounded from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRounded from '@mui/icons-material/KeyboardArrowUpRounded';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRounded from '@mui/icons-material/ArrowForwardRounded';
import ArrowOutwardRounded from '@mui/icons-material/ArrowOutwardRounded';
import UndoRounded from '@mui/icons-material/UndoRounded';
import RedoRounded from '@mui/icons-material/RedoRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import OpenInNewRounded from '@mui/icons-material/OpenInNewRounded';
import PowerSettingsNewRounded from '@mui/icons-material/PowerSettingsNewRounded';
import LanguageOutlined from '@mui/icons-material/LanguageOutlined';
import LightModeOutlined from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlined from '@mui/icons-material/DarkModeOutlined';
import NotificationsNoneRounded from '@mui/icons-material/NotificationsNoneRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditOutlined from '@mui/icons-material/EditOutlined';
import ArchiveOutlined from '@mui/icons-material/ArchiveOutlined';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import UnarchiveOutlined from '@mui/icons-material/UnarchiveOutlined';
import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import AddCommentOutlined from '@mui/icons-material/AddCommentOutlined';
import ReplyRounded from '@mui/icons-material/ReplyRounded';
import OpenInFullRounded from '@mui/icons-material/OpenInFullRounded';
import CloseFullscreenRounded from '@mui/icons-material/CloseFullscreenRounded';
import ContentCopyRounded from '@mui/icons-material/ContentCopyRounded';
import FileDownloadOutlined from '@mui/icons-material/FileDownloadOutlined';
import BorderColorOutlined from '@mui/icons-material/BorderColorOutlined';

/* ------------------------------ Objects & status ----------------------------- */
import TagRounded from '@mui/icons-material/TagRounded';
import LockOutlined from '@mui/icons-material/LockOutlined';
import LockOpenOutlined from '@mui/icons-material/LockOpenOutlined';
import BlockRounded from '@mui/icons-material/BlockRounded';
import RepeatRounded from '@mui/icons-material/RepeatRounded';
import AccountTreeOutlined from '@mui/icons-material/AccountTreeOutlined';
import ChecklistRounded from '@mui/icons-material/ChecklistRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import ScheduleRounded from '@mui/icons-material/ScheduleRounded';
import CalendarMonthOutlined from '@mui/icons-material/CalendarMonthOutlined';
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded';
import PersonAddAltOutlined from '@mui/icons-material/PersonAddAltOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import ApartmentOutlined from '@mui/icons-material/ApartmentOutlined';
import ElevatorOutlined from '@mui/icons-material/ElevatorOutlined';
import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined';
import MenuBookOutlined from '@mui/icons-material/MenuBookOutlined';
import ChairOutlined from '@mui/icons-material/ChairOutlined';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import WavingHandOutlined from '@mui/icons-material/WavingHandOutlined';
import PanToolOutlined from '@mui/icons-material/PanToolOutlined';
import DirectionsWalkRounded from '@mui/icons-material/DirectionsWalkRounded';
import DirectionsRunRounded from '@mui/icons-material/DirectionsRunRounded';
import MeetingRoomRounded from '@mui/icons-material/MeetingRoomRounded';
import InsertChartOutlinedRounded from '@mui/icons-material/InsertChartOutlinedRounded';
import LocalFireDepartmentOutlined from '@mui/icons-material/LocalFireDepartmentOutlined';
import EmojiEventsOutlined from '@mui/icons-material/EmojiEventsOutlined';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import TimerOutlined from '@mui/icons-material/TimerOutlined';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import RadioButtonUncheckedRounded from '@mui/icons-material/RadioButtonUncheckedRounded';
import CheckBoxOutlined from '@mui/icons-material/CheckBoxOutlined';
import CheckBoxOutlineBlankRounded from '@mui/icons-material/CheckBoxOutlineBlankRounded';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import AlternateEmailRounded from '@mui/icons-material/AlternateEmailRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import MarkEmailReadOutlined from '@mui/icons-material/MarkEmailReadOutlined';
import DnsOutlined from '@mui/icons-material/DnsOutlined';
import EventOutlined from '@mui/icons-material/EventOutlined';

/* ---------------------------------- Media ----------------------------------- */
import MicNoneRounded from '@mui/icons-material/MicNoneRounded';
import SettingsInputAntennaRounded from '@mui/icons-material/SettingsInputAntennaRounded';
import MicOffOutlined from '@mui/icons-material/MicOffOutlined';
import VideocamOffOutlined from '@mui/icons-material/VideocamOffOutlined';
import ScreenShareOutlined from '@mui/icons-material/ScreenShareOutlined';
import StopScreenShareOutlined from '@mui/icons-material/StopScreenShareOutlined';
import FiberManualRecordRounded from '@mui/icons-material/FiberManualRecordRounded';
import StopRounded from '@mui/icons-material/StopRounded';
import VolumeUpOutlined from '@mui/icons-material/VolumeUpOutlined';
import VolumeOffOutlined from '@mui/icons-material/VolumeOffOutlined';
import EmojiEmotionsOutlined from '@mui/icons-material/EmojiEmotionsOutlined';
import AddPhotoAlternateOutlined from '@mui/icons-material/AddPhotoAlternateOutlined';
import CallOutlined from '@mui/icons-material/CallOutlined';
import PictureInPictureAltOutlined from '@mui/icons-material/PictureInPictureAltOutlined';
import SignalCellularAltRounded from '@mui/icons-material/SignalCellularAltRounded';
import SignalCellularAlt2BarRounded from '@mui/icons-material/SignalCellularAlt2BarRounded';
import SignalCellularAlt1BarRounded from '@mui/icons-material/SignalCellularAlt1BarRounded';
import SwapHorizRounded from '@mui/icons-material/SwapHorizRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import LinkRounded from '@mui/icons-material/LinkRounded';

/* --------------------------------- File kinds -------------------------------- */
import ImageOutlined from '@mui/icons-material/ImageOutlined';
import MovieOutlined from '@mui/icons-material/MovieOutlined';
import AudiotrackOutlined from '@mui/icons-material/AudiotrackOutlined';
import PictureAsPdfOutlined from '@mui/icons-material/PictureAsPdfOutlined';
import FolderZipOutlined from '@mui/icons-material/FolderZipOutlined';
import TableChartOutlined from '@mui/icons-material/TableChartOutlined';
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined';

/* -------------------------------- Editor tools ------------------------------- */
import FormatBoldRounded from '@mui/icons-material/FormatBoldRounded';
import FormatItalicRounded from '@mui/icons-material/FormatItalicRounded';
import StrikethroughSRounded from '@mui/icons-material/StrikethroughSRounded';
import FormatListBulletedRounded from '@mui/icons-material/FormatListBulletedRounded';
import FormatListNumberedRounded from '@mui/icons-material/FormatListNumberedRounded';
import FormatQuoteRounded from '@mui/icons-material/FormatQuoteRounded';
import CodeRounded from '@mui/icons-material/CodeRounded';
import TitleRounded from '@mui/icons-material/TitleRounded';
import HorizontalRuleRounded from '@mui/icons-material/HorizontalRuleRounded';
import FormatAlignLeftRounded from '@mui/icons-material/FormatAlignLeftRounded';
import FormatAlignCenterRounded from '@mui/icons-material/FormatAlignCenterRounded';
import FormatAlignRightRounded from '@mui/icons-material/FormatAlignRightRounded';
import FormatColorFillRounded from '@mui/icons-material/FormatColorFillRounded';
import FormatColorTextRounded from '@mui/icons-material/FormatColorTextRounded';
import FormatClearRounded from '@mui/icons-material/FormatClearRounded';
import FunctionsRounded from '@mui/icons-material/FunctionsRounded';
import TableRowsRounded from '@mui/icons-material/TableRowsRounded';
import ViewColumnRounded from '@mui/icons-material/ViewColumnRounded';
import SlideshowRounded from '@mui/icons-material/SlideshowRounded';
import SpeakerNotesOutlined from '@mui/icons-material/SpeakerNotesOutlined';

/* ------------------------------ Whiteboard tools ----------------------------- */
import NearMeOutlined from '@mui/icons-material/NearMeOutlined';
import RectangleOutlined from '@mui/icons-material/RectangleOutlined';
import CircleOutlined from '@mui/icons-material/CircleOutlined';
import StickyNote2Outlined from '@mui/icons-material/StickyNote2Outlined';
import TextFieldsRounded from '@mui/icons-material/TextFieldsRounded';
import BackspaceOutlined from '@mui/icons-material/BackspaceOutlined';

/** The four steps of the icon scale. `md` is the default for UI chrome. */
export const ICON_SIZES = { xs: 14, sm: 16, md: 20, lg: 24 } as const;

export interface IconProps {
  /** A step on the icon scale, or an explicit pixel size. Defaults to `md`. */
  size?: keyof typeof ICON_SIZES | number;
  className?: string;
  /**
   * Accessible name. Supply this only when no adjacent label or `aria-label`
   * already names the control — otherwise the icon stays `aria-hidden`.
   */
  title?: string;
}

/**
 * MUI icons size themselves off `font-size`, so Tailwind's `h-*`/`w-*` classes
 * do not reach them. An inline `fontSize` always wins and costs nothing.
 */
function wrap(Source: SvgIconComponent, displayName: string) {
  function Icon({ size = 'md', className, title }: IconProps) {
    const px = typeof size === 'number' ? size : ICON_SIZES[size];
    return (
      <Source
        style={{ fontSize: px }}
        className={className}
        focusable={false}
        {...(title ? { titleAccess: title } : { 'aria-hidden': true })}
      />
    );
  }
  Icon.displayName = displayName;
  return Icon;
}

/** Navigation */
export const HomeIcon = wrap(HomeOutlined, 'HomeIcon');
export const SpacesIcon = wrap(MeetingRoomOutlined, 'SpacesIcon');
export const ProjectsIcon = wrap(ViewKanbanOutlined, 'ProjectsIcon');
export const TasksIcon = wrap(TaskAltOutlined, 'TasksIcon');
export const AutomationsIcon = wrap(BoltOutlined, 'AutomationsIcon');
export const ChatIcon = wrap(ForumOutlined, 'ChatIcon');
export const MeetingsIcon = wrap(VideocamOutlined, 'MeetingsIcon');
export const WhiteboardIcon = wrap(DrawOutlined, 'WhiteboardIcon');
export const FilesIcon = wrap(FolderOutlined, 'FilesIcon');
/** The file vault — a box with something in it, rather than another padlock. */
export const VaultIcon = wrap(Inventory2Outlined, 'VaultIcon');
/* The vault's shelves. Documents reuse `FileDocIcon`, further down: one
   concept, one drawing. */
export const CardIcon = wrap(CreditCardOutlined, 'CardIcon');
export const IdIcon = wrap(BadgeOutlined, 'IdIcon');
export const AssetIcon = wrap(DiamondOutlined, 'AssetIcon');
export const KeyIcon = wrap(KeyOutlined, 'KeyIcon');
export const PhotosIcon = wrap(PhotoLibraryOutlined, 'PhotosIcon');
export const GamesIcon = wrap(SportsEsportsOutlined, 'GamesIcon');
export const PeopleIcon = wrap(PeopleAltOutlined, 'PeopleIcon');
export const SettingsIcon = wrap(SettingsOutlined, 'SettingsIcon');
export const WorkspaceIcon = wrap(CloudOutlined, 'WorkspaceIcon');

/** Controls */
export const MenuIcon = wrap(MenuRounded, 'MenuIcon');
export const SearchIcon = wrap(SearchRounded, 'SearchIcon');
/** The two ways to lay a library out, and the control that reorders it. */
export const GridViewIcon = wrap(GridViewRounded, 'GridViewIcon');
export const ListViewIcon = wrap(ViewListRounded, 'ListViewIcon');
export const SortIcon = wrap(SwapVertRounded, 'SortIcon');
export const CloseIcon = wrap(CloseRounded, 'CloseIcon');
export const PlusIcon = wrap(AddRounded, 'PlusIcon');
export const MinusIcon = wrap(RemoveRounded, 'MinusIcon');
export const CheckIcon = wrap(CheckRounded, 'CheckIcon');
export const MoreIcon = wrap(MoreHorizRounded, 'MoreIcon');
export const GripIcon = wrap(DragIndicatorRounded, 'GripIcon');
export const SendIcon = wrap(SendRounded, 'SendIcon');
export const ChevronDownIcon = wrap(KeyboardArrowDownRounded, 'ChevronDownIcon');
export const ChevronUpIcon = wrap(KeyboardArrowUpRounded, 'ChevronUpIcon');
export const ChevronLeftIcon = wrap(ChevronLeftRounded, 'ChevronLeftIcon');
export const ChevronRightIcon = wrap(ChevronRightRounded, 'ChevronRightIcon');
export const ArrowBackIcon = wrap(ArrowBackRounded, 'ArrowBackIcon');
export const ArrowForwardIcon = wrap(ArrowForwardRounded, 'ArrowForwardIcon');
export const ArrowOutwardIcon = wrap(ArrowOutwardRounded, 'ArrowOutwardIcon');
export const UndoIcon = wrap(UndoRounded, 'UndoIcon');
export const RedoIcon = wrap(RedoRounded, 'RedoIcon');
export const SignOutIcon = wrap(LogoutRounded, 'SignOutIcon');
export const OpenInNewIcon = wrap(OpenInNewRounded, 'OpenInNewIcon');
export const LeaveIcon = wrap(PowerSettingsNewRounded, 'LeaveIcon');
export const LanguageIcon = wrap(LanguageOutlined, 'LanguageIcon');
export const LightThemeIcon = wrap(LightModeOutlined, 'LightThemeIcon');
export const DarkThemeIcon = wrap(DarkModeOutlined, 'DarkThemeIcon');
export const BellIcon = wrap(NotificationsNoneRounded, 'BellIcon');
export const TrashIcon = wrap(DeleteOutlineRounded, 'TrashIcon');
export const EditIcon = wrap(EditOutlined, 'EditIcon');
export const ArchiveIcon = wrap(ArchiveOutlined, 'ArchiveIcon');
export const UnarchiveIcon = wrap(UnarchiveOutlined, 'UnarchiveIcon');
export const AttachIcon = wrap(AttachFileRounded, 'AttachIcon');
export const NewMessageIcon = wrap(AddCommentOutlined, 'NewMessageIcon');
export const ReplyIcon = wrap(ReplyRounded, 'ReplyIcon');
export const ExpandIcon = wrap(OpenInFullRounded, 'ExpandIcon');
export const CollapseIcon = wrap(CloseFullscreenRounded, 'CollapseIcon');
export const CopyIcon = wrap(ContentCopyRounded, 'CopyIcon');
export const DownloadIcon = wrap(FileDownloadOutlined, 'DownloadIcon');
export const HighlighterIcon = wrap(BorderColorOutlined, 'HighlighterIcon');

/** Objects and status */
export const ChannelIcon = wrap(TagRounded, 'ChannelIcon');
export const LockIcon = wrap(LockOutlined, 'LockIcon');
export const UnlockIcon = wrap(LockOpenOutlined, 'UnlockIcon');
export const BlockedIcon = wrap(BlockRounded, 'BlockedIcon');
export const RepeatIcon = wrap(RepeatRounded, 'RepeatIcon');
export const SubtaskIcon = wrap(AccountTreeOutlined, 'SubtaskIcon');
export const MilestoneIcon = wrap(FlagOutlined, 'MilestoneIcon');
export const ChecklistIcon = wrap(ChecklistRounded, 'ChecklistIcon');
export const CommentIcon = wrap(ChatBubbleOutlineRounded, 'CommentIcon');
export const ClockIcon = wrap(ScheduleRounded, 'ClockIcon');
export const CalendarIcon = wrap(CalendarMonthOutlined, 'CalendarIcon');
export const PersonIcon = wrap(PersonOutlineRounded, 'PersonIcon');
export const InviteGuestIcon = wrap(PersonAddAltOutlined, 'InviteGuestIcon');
export const GroupIcon = wrap(GroupsOutlined, 'GroupIcon');
export const CompanyIcon = wrap(ApartmentOutlined, 'CompanyIcon');
export const ElevatorIcon = wrap(ElevatorOutlined, 'ElevatorIcon');
export const MoneyIcon = wrap(PaymentsOutlined, 'MoneyIcon');
export const BudgetIcon = wrap(AccountBalanceWalletOutlined, 'BudgetIcon');
export const BookIcon = wrap(MenuBookOutlined, 'BookIcon');
export const FurnitureIcon = wrap(ChairOutlined, 'FurnitureIcon');
export const SparkleIcon = wrap(AutoAwesomeOutlined, 'SparkleIcon');
export const WaveIcon = wrap(WavingHandOutlined, 'WaveIcon');
export const KnockIcon = wrap(PanToolOutlined, 'KnockIcon');
export const WalkIcon = wrap(DirectionsWalkRounded, 'WalkIcon');
export const JumpIcon = wrap(DirectionsRunRounded, 'JumpIcon');
export const TeleportIcon = wrap(MeetingRoomRounded, 'TeleportIcon');
export const ReportIcon = wrap(InsertChartOutlinedRounded, 'ReportIcon');
/* The engagement loop: the streak flame carries the whole feature's identity,
   so it is also the navigation icon. */
export const FlameIcon = wrap(LocalFireDepartmentOutlined, 'FlameIcon');
export const TrophyIcon = wrap(EmojiEventsOutlined, 'TrophyIcon');
export const StoreIcon = wrap(StorefrontOutlined, 'StoreIcon');
export const FocusIcon = wrap(TimerOutlined, 'FocusIcon');
export const CheckCircleIcon = wrap(CheckCircleOutlineRounded, 'CheckCircleIcon');
export const CircleIcon = wrap(RadioButtonUncheckedRounded, 'CircleIcon');
export const CheckboxOnIcon = wrap(CheckBoxOutlined, 'CheckboxOnIcon');
export const CheckboxOffIcon = wrap(CheckBoxOutlineBlankRounded, 'CheckboxOffIcon');
export const InfoIcon = wrap(InfoOutlined, 'InfoIcon');
export const AlertIcon = wrap(ErrorOutlineRounded, 'AlertIcon');
export const EyeIcon = wrap(VisibilityOutlined, 'EyeIcon');
export const EyeOffIcon = wrap(VisibilityOffOutlined, 'EyeOffIcon');
export const MentionIcon = wrap(AlternateEmailRounded, 'MentionIcon');
export const MailIcon = wrap(MailOutlineRounded, 'MailIcon');
export const RefreshIcon = wrap(RefreshRounded, 'RefreshIcon');
/* Two drawings of one concept, which is the exception this file allows: a
   flagged message and an unflagged one have to be told apart at a glance in a
   list, and outline-versus-filled is the only difference small enough to do
   that without a second column. */
export const StarIcon = wrap(StarBorderRounded, 'StarIcon');
export const StarFilledIcon = wrap(StarRounded, 'StarFilledIcon');
export const MailboxIcon = wrap(MarkEmailReadOutlined, 'MailboxIcon');
export const DnsIcon = wrap(DnsOutlined, 'DnsIcon');
export const EventIcon = wrap(EventOutlined, 'EventIcon');

/** Media */
export const MicIcon = wrap(MicNoneRounded, 'MicIcon');
/* The walkie talkie, wherever it is named rather than drawn as its button. */
export const RadioIcon = wrap(SettingsInputAntennaRounded, 'RadioIcon');
export const MicOffIcon = wrap(MicOffOutlined, 'MicOffIcon');
export const CameraIcon = wrap(VideocamOutlined, 'CameraIcon');
export const CameraOffIcon = wrap(VideocamOffOutlined, 'CameraOffIcon');
export const ScreenShareIcon = wrap(ScreenShareOutlined, 'ScreenShareIcon');
export const ScreenShareOffIcon = wrap(StopScreenShareOutlined, 'ScreenShareOffIcon');
/* The red dot of a meeting recording, and the square that stops it. */
export const RecordIcon = wrap(FiberManualRecordRounded, 'RecordIcon');
export const StopIcon = wrap(StopRounded, 'StopIcon');
export const SpeakerIcon = wrap(VolumeUpOutlined, 'SpeakerIcon');
export const SpeakerOffIcon = wrap(VolumeOffOutlined, 'SpeakerOffIcon');
export const EmojiIcon = wrap(EmojiEmotionsOutlined, 'EmojiIcon');
export const ImageUploadIcon = wrap(AddPhotoAlternateOutlined, 'ImageUploadIcon');
export const CallIcon = wrap(CallOutlined, 'CallIcon');
export const PipIcon = wrap(PictureInPictureAltOutlined, 'PipIcon');
/** Link health, strongest to weakest. */
export const SignalGoodIcon = wrap(SignalCellularAltRounded, 'SignalGoodIcon');
export const SignalFairIcon = wrap(SignalCellularAlt2BarRounded, 'SignalFairIcon');
export const SignalPoorIcon = wrap(SignalCellularAlt1BarRounded, 'SignalPoorIcon');
/** Media routed through a TURN relay rather than peer to peer. */
export const RelayIcon = wrap(SwapHorizRounded, 'RelayIcon');
export const DeviceSettingsIcon = wrap(TuneRounded, 'DeviceSettingsIcon');
export const LinkIcon = wrap(LinkRounded, 'LinkIcon');

/** File kinds */
export const FileImageIcon = wrap(ImageOutlined, 'FileImageIcon');
export const FileVideoIcon = wrap(MovieOutlined, 'FileVideoIcon');
export const FileAudioIcon = wrap(AudiotrackOutlined, 'FileAudioIcon');
export const FilePdfIcon = wrap(PictureAsPdfOutlined, 'FilePdfIcon');
export const FileArchiveIcon = wrap(FolderZipOutlined, 'FileArchiveIcon');
export const FileSheetIcon = wrap(TableChartOutlined, 'FileSheetIcon');
export const FileDocIcon = wrap(DescriptionOutlined, 'FileDocIcon');
export const FileGenericIcon = wrap(AttachFileRounded, 'FileGenericIcon');

/** Whiteboard tools */
export const SelectToolIcon = wrap(NearMeOutlined, 'SelectToolIcon');
export const PenToolIcon = wrap(DrawOutlined, 'PenToolIcon');
export const RectToolIcon = wrap(RectangleOutlined, 'RectToolIcon');
export const EllipseToolIcon = wrap(CircleOutlined, 'EllipseToolIcon');
export const ArrowToolIcon = wrap(ArrowOutwardRounded, 'ArrowToolIcon');
export const NoteToolIcon = wrap(StickyNote2Outlined, 'NoteToolIcon');
export const TextToolIcon = wrap(TextFieldsRounded, 'TextToolIcon');
export const EraserToolIcon = wrap(BackspaceOutlined, 'EraserToolIcon');

/**
 * Document, spreadsheet and deck tools.
 *
 * Named for what they do to the thing being edited rather than for the glyph,
 * so the toolbars read as the actions people already know from every other
 * editor: bold is `BoldIcon`, not `FormatBoldIcon`.
 */
export const BoldIcon = wrap(FormatBoldRounded, 'BoldIcon');
export const ItalicIcon = wrap(FormatItalicRounded, 'ItalicIcon');
export const StrikethroughIcon = wrap(StrikethroughSRounded, 'StrikethroughIcon');
export const BulletListIcon = wrap(FormatListBulletedRounded, 'BulletListIcon');
export const NumberListIcon = wrap(FormatListNumberedRounded, 'NumberListIcon');
export const QuoteIcon = wrap(FormatQuoteRounded, 'QuoteIcon');
export const CodeIcon = wrap(CodeRounded, 'CodeIcon');
export const HeadingIcon = wrap(TitleRounded, 'HeadingIcon');
export const DividerIcon = wrap(HorizontalRuleRounded, 'DividerIcon');
export const AlignLeftIcon = wrap(FormatAlignLeftRounded, 'AlignLeftIcon');
export const AlignCenterIcon = wrap(FormatAlignCenterRounded, 'AlignCenterIcon');
export const AlignRightIcon = wrap(FormatAlignRightRounded, 'AlignRightIcon');
export const FillColorIcon = wrap(FormatColorFillRounded, 'FillColorIcon');
export const TextColorIcon = wrap(FormatColorTextRounded, 'TextColorIcon');
export const ClearFormatIcon = wrap(FormatClearRounded, 'ClearFormatIcon');
export const FormulaIcon = wrap(FunctionsRounded, 'FormulaIcon');
export const AddRowsIcon = wrap(TableRowsRounded, 'AddRowsIcon');
export const AddColumnsIcon = wrap(ViewColumnRounded, 'AddColumnsIcon');
export const PresentIcon = wrap(SlideshowRounded, 'PresentIcon');
export const SpeakerNotesIcon = wrap(SpeakerNotesOutlined, 'SpeakerNotesIcon');
